import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { FileText, Upload, Trash2 } from 'lucide-react';

var API_URL = process.env.REACT_APP_BACKEND_URL;

export function EmployeeFilesView(props) {
  var employeeId = props.employeeId;
  var [files, setFiles] = useState([]);
  var [loading, setLoading] = useState(true);
  var [uploading, setUploading] = useState(false);
  var [category, setCategory] = useState('work_report');
  var [description, setDescription] = useState('');
  var [vendorName, setVendorName] = useState('');
  var [vendorLocation, setVendorLocation] = useState('');
  var [vendorPhone, setVendorPhone] = useState('');

  useEffect(function() {
    fetchFiles();
  }, [employeeId]);

  function fetchFiles() {
    setLoading(true);
    fetch(API_URL + '/api/employee/files/' + employeeId)
      .then(function(r) { return r.json(); })
      .then(function(data) {
        setFiles(data.files || []);
        setLoading(false);
      })
      .catch(function() { setLoading(false); });
  }

  function handleUpload(e) {
    var file = e.target.files[0];
    if (!file) return;

    var ext = file.name.split('.').pop().toLowerCase();
    var allowed = ['pdf', 'jpg', 'jpeg', 'png', 'xlsx', 'xls', 'csv'];
    if (allowed.indexOf(ext) === -1) {
      toast.error('File type not allowed. Use PDF, JPEG, PNG, or Excel files.');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large. Maximum 10MB.');
      return;
    }

    setUploading(true);
    var formData = new FormData();
    formData.append('file', file);
    formData.append('file_category', category);
    formData.append('description', description);
    if (vendorName) formData.append('vendor_name', vendorName);
    if (vendorLocation) formData.append('vendor_location', vendorLocation);
    if (vendorPhone) formData.append('vendor_phone', vendorPhone);

    fetch(API_URL + '/api/employee/files/upload/' + employeeId, {
      method: 'POST',
      body: formData
    })
      .then(function(r) { return r.json(); })
      .then(function(data) {
        if (data.success) {
          toast.success('File uploaded successfully');
          setDescription('');
          setVendorName('');
          setVendorLocation('');
          setVendorPhone('');
          fetchFiles();
        } else {
          toast.error(data.detail || 'Upload failed');
        }
        setUploading(false);
      })
      .catch(function() {
        toast.error('Upload failed');
        setUploading(false);
      });
    
    e.target.value = '';
  }

  function handleDelete(fileId) {
    if (!window.confirm('Delete this file?')) return;
    
    fetch(API_URL + '/api/employee/files/' + fileId, { method: 'DELETE' })
      .then(function(r) { return r.json(); })
      .then(function(data) {
        if (data.success) {
          toast.success('File deleted');
          fetchFiles();
        }
      });
  }

  function formatFileSize(bytes) {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  }

  function getFileIcon(contentType, filename) {
    if (contentType && contentType.includes('pdf')) return '📄';
    if (contentType && contentType.includes('image')) return '🖼️';
    if (contentType && (contentType.includes('sheet') || contentType.includes('excel'))) return '📊';
    if (filename && filename.endsWith('.csv')) return '📊';
    return '📁';
  }

  var categoryBadges = {
    'work_report': { label: 'Work Report', color: 'bg-blue-500/20 text-blue-300' },
    'expense_receipt': { label: 'Expense Receipt', color: 'bg-green-500/20 text-green-300' },
    'vendor_visit': { label: 'Vendor/DP Visit', color: 'bg-amber-500/20 text-amber-300' },
    'other': { label: 'Other', color: 'bg-gray-500/20 text-gray-300' }
  };

  var fileItems = files.map(function(f) {
    var badge = categoryBadges[f.file_category] || categoryBadges.other;
    var hasVendorInfo = f.vendor_name || f.vendor_location || f.vendor_phone;
    return (
      <div key={f.id} className="p-3 bg-slate-700/30 rounded-lg hover:bg-slate-700/50 transition-colors" data-testid={'file-' + f.id}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-2xl">{getFileIcon(f.content_type, f.filename)}</span>
            <div>
              <p className="text-white font-medium text-sm">{f.filename}</p>
              <div className="flex items-center gap-2 mt-1">
                <Badge className={badge.color}>{badge.label}</Badge>
                <span className="text-xs text-gray-400">{formatFileSize(f.file_size)}</span>
                <span className="text-xs text-gray-500">{new Date(f.uploaded_at).toLocaleDateString()}</span>
              </div>
              {f.description && <p className="text-xs text-gray-400 mt-1">{f.description}</p>}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <a href={f.file_url} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 text-sm">View</a>
            <Button size="sm" variant="ghost" onClick={function() { handleDelete(f.id); }} className="text-red-400 hover:text-red-300 hover:bg-red-500/10">
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
        {hasVendorInfo && (
          <div className="mt-2 pt-2 border-t border-white/10 flex items-center gap-4 text-xs">
            {f.vendor_name && <span className="text-amber-300">📋 {f.vendor_name}</span>}
            {f.vendor_location && <span className="text-gray-400">📍 {f.vendor_location}</span>}
            {f.vendor_phone && <span className="text-gray-400">📞 {f.vendor_phone}</span>}
          </div>
        )}
      </div>
    );
  });

  return (
    <Card className="bg-slate-800/50 border-white/10">
      <CardHeader>
        <CardTitle className="text-white flex items-center gap-2">
          <FileText className="w-5 h-5 text-purple-400" />
          My Files
        </CardTitle>
        <p className="text-sm text-gray-400">Upload work reports, receipts, spreadsheets, and other documents</p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="p-4 bg-gradient-to-r from-purple-900/30 to-blue-900/30 rounded-lg border border-purple-500/30">
          <p className="text-white font-medium mb-3">Upload New File</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="bg-slate-700/50 border-white/10 text-white" data-testid="file-category-select">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-white/10">
                <SelectItem value="work_report">Work Report</SelectItem>
                <SelectItem value="expense_receipt">Expense Receipt</SelectItem>
                <SelectItem value="vendor_visit">Vendor/DP Visit</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
            <Input 
              placeholder="Description (optional)" 
              value={description} 
              onChange={function(e) { setDescription(e.target.value); }}
              className="bg-slate-700/50 border-white/10 text-white md:col-span-2"
              data-testid="file-description"
            />
          </div>
          
          {/* Optional Vendor/DP Details */}
          <div className="p-3 bg-slate-700/30 rounded-lg mb-3">
            <p className="text-sm text-gray-400 mb-2">Vendor/DP Details (Optional)</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Input 
                placeholder="Vendor/DP Name" 
                value={vendorName} 
                onChange={function(e) { setVendorName(e.target.value); }}
                className="bg-slate-700/50 border-white/10 text-white"
                data-testid="vendor-name"
              />
              <Input 
                placeholder="Location" 
                value={vendorLocation} 
                onChange={function(e) { setVendorLocation(e.target.value); }}
                className="bg-slate-700/50 border-white/10 text-white"
                data-testid="vendor-location"
              />
              <Input 
                placeholder="Phone Number" 
                value={vendorPhone} 
                onChange={function(e) { setVendorPhone(e.target.value); }}
                className="bg-slate-700/50 border-white/10 text-white"
                data-testid="vendor-phone"
              />
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <label className="flex-1">
              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.xlsx,.xls,.csv"
                onChange={handleUpload}
                disabled={uploading}
                className="hidden"
                data-testid="file-input"
              />
              <div className={"flex items-center justify-center gap-2 p-3 border-2 border-dashed rounded-lg cursor-pointer transition-colors " + (uploading ? "border-gray-500 bg-gray-800/50" : "border-purple-500/50 hover:border-purple-400 hover:bg-purple-500/10")}>
                {uploading ? (
                  <div className="w-5 h-5 border-2 border-purple-500/30 border-t-purple-500 rounded-full animate-spin"></div>
                ) : (
                  <Upload className="w-5 h-5 text-purple-400" />
                )}
                <span className="text-white">{uploading ? 'Uploading...' : 'Click to upload file'}</span>
              </div>
            </label>
          </div>
          <p className="text-xs text-gray-400 mt-2">Allowed: PDF, JPEG, PNG, Excel (max 10MB)</p>
        </div>

        {loading ? (
          <div className="flex justify-center py-8">
            <div className="w-8 h-8 border-4 border-purple-500/30 border-t-purple-500 rounded-full animate-spin"></div>
          </div>
        ) : files.length > 0 ? (
          <div className="space-y-2">{fileItems}</div>
        ) : (
          <div className="text-center py-8">
            <FileText className="w-12 h-12 text-gray-600 mx-auto mb-2" />
            <p className="text-gray-400">No files uploaded yet</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
